'use client'

import { useState } from 'react'
import dynamic from 'next/dynamic'
import { Grid, Table, TableHead, TableBody, TableFooter, TableRow, TableCell, Alert } from '@mui/material'
import { Trans } from 'react-i18next'

// Types
import type { MouseEvent } from 'react'
import type { TransProps } from '@/types/components/atoms/text/trans'

// Componenets
const IconAtom = dynamic(() => import('@/components/atoms/icons'))
const ImageAtom = dynamic(() => import('@/components/atoms/items/image'))

const TransAtom = (props: TransProps) => {
	// Props
	const { components, children } = props

	// Variables
	const [orderBy, setOrderBy] = useState<number>(-1)
	const [order, setOrder] = useState<'asc' | 'desc'>('asc')

	// Callbacks
	const handleClick = (event: MouseEvent<unknown>) => {
		const target = event.target as HTMLElement
		const parent = target.closest('td') as HTMLTableCellElement

		if (target && (target.classList.contains('editor-table-cell-header') || parent.classList.contains('editor-table-cell-header'))) {
			const tbody = target.closest('tbody')
			const cellIndex: number = parent.cellIndex ?? 0
			const newOrder = orderBy === cellIndex ? (order === 'asc' ? 'desc' : 'asc') : 'asc'
			setOrderBy(cellIndex)
			setOrder(newOrder)

			if (tbody) {
				// Add classes
				parent.classList.remove('selected')
				parent.classList.remove('order-asc')
				parent.classList.remove('order-desc')
				parent.classList.add('selected', `order-${newOrder}`)

				parent
					.closest('tr')
					?.querySelectorAll('td')
					.forEach(td => {
						if (td !== parent) {
							td.classList.remove('selected')
							td.classList.remove('order-asc')
							td.classList.remove('order-desc')
						}
					})

				// Sort
				const rows = Array.from(tbody.querySelectorAll('tr')).slice(1)
				const sortedRows: { content: string; index: number }[] = []

				rows.forEach((row, index) => {
					const content = row.querySelector(`td:nth-child(${cellIndex + 1})`)?.textContent?.trim() ?? ''
					sortedRows.push({ content, index })
				})

				sortedRows.sort((a, b) => {
					if (newOrder === 'asc') return a.content.localeCompare(b.content)
					else return b.content.localeCompare(a.content)
				})

				sortedRows.reverse().forEach(row => {
					tbody.insertBefore(rows[row.index], tbody.querySelector('tr:nth-child(2)'))
				})
			}
		}

		event.preventDefault()
		event.stopPropagation()
	}

	return (
		<Trans
			components={{
				...{
					a: <a />,
					abbr: <abbr />,
					address: <address />,
					alert: <Alert severity="warning" />,
					area: <area />,
					article: <article />,
					aside: <aside />,
					audio: <audio />,
					b: <b />,
					base: <base />,
					bdi: <bdi />,
					bdo: <bdo />,
					big: <big />,
					blockquote: <blockquote />,
					body: <body />,
					br: <br />,
					button: <button />,
					canvas: <canvas />,
					caption: <caption />,
					center: <center />,
					cite: <cite />,
					code: <code />,
					col: <col />,
					colgroup: <colgroup />,
					data: <data />,
					datalist: <datalist />,
					dd: <dd />,
					del: <del />,
					details: <details />,
					dfn: <dfn />,
					dialog: <dialog />,
					div: <div />,
					dl: <dl />,
					dt: <dt />,
					em: <em />,
					embed: <embed />,
					fieldset: <fieldset />,
					figcaption: <figcaption />,
					figure: <figure />,
					footer: <footer />,
					form: <form />,
					grid: <Grid width="100%" />,
					h1: <h1 />,
					h2: <h2 />,
					h3: <h3 />,
					h4: <h4 />,
					h5: <h5 />,
					h6: <h6 />,
					header: <header />,
					hgroup: <hgroup />,
					hr: <hr />,
					html: <html />,
					i: <i />,
					iframe: <iframe />,
					input: <input />,
					ins: <ins />,
					kbd: <kbd />,
					keygen: <keygen />,
					label: <label />,
					legend: <legend />,
					li: <li />,
					link: <link />,
					main: <main />,
					map: <map />,
					mark: <mark />,
					menu: <menu />,
					menuitem: <menuitem />,
					meta: <meta />,
					meter: <meter />,
					nav: <nav />,
					noindex: <noindex />,
					noscript: <noscript />,
					object: <object />,
					ol: <ol />,
					optgroup: <optgroup />,
					option: <option />,
					output: <output />,
					p: <p />,
					param: <param />,
					picture: <picture />,
					pre: <pre />,
					progress: <progress />,
					q: <q />,
					rp: <rp />,
					rt: <rt />,
					ruby: <ruby />,
					s: <s />,
					samp: <samp />,
					search: <search />,
					slot: <slot />,
					script: <script />,
					section: <section />,
					select: <select />,
					small: <small />,
					source: <source />,
					span: <span />,
					strong: <strong />,
					style: <style />,
					sub: <sub />,
					summary: <summary />,
					sup: <sup />,
					table: <Table sx={{ borderWidth: 1 }} />,
					template: <template />,
					tbody: <TableBody />,
					td: <TableCell />,
					textarea: <textarea />,
					tfoot: <TableFooter />,
					th: <TableCell />,
					thead: <TableHead />,
					time: <time />,
					title: <title />,
					tr: <TableRow onClick={event => handleClick(event)} />,
					track: <track />,
					u: <u />,
					ul: <ul />,
					video: <video />,
					wbr: <wbr />,
					webview: <webview />,
					icon: <IconAtom />,
					img: <ImageAtom />
				},
				...(components ? components : {})
			}}
		>
			{children}
		</Trans>
	)
}

export default TransAtom
